import React from 'react';
import { Input, Button } from '@heroui/react';
import { Search, X, Filter } from 'lucide-react';

export interface FiltersBarProps {
  title: string;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  searchPlaceholder?: string;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  clearFiltersLabel: string;
  children: React.ReactNode;
  gridCols?: '2' | '3' | '4';
}

const FiltersBar: React.FC<FiltersBarProps> = ({
  title,
  searchQuery,
  onSearchChange,
  searchPlaceholder = 'Search...',
  hasActiveFilters,
  onClearFilters,
  clearFiltersLabel,
  children,
  gridCols = '3',
}) => {
  const gridColsClass = {
    '2': 'sm:grid-cols-2',
    '3': 'sm:grid-cols-2 lg:grid-cols-3',
    '4': 'sm:grid-cols-2 lg:grid-cols-4',
  }[gridCols];

  return (
    <div className="relative">
      {/* Glassmorphism background */}
      <div className="absolute inset-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-800/50 shadow-lg" />

      <div className="relative p-6 md:p-8 mb-8">
        {/* Header */}
        <div className="flex items-center gap-2 mb-6">
          <div className="p-2 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600">
            <Filter className="w-4 h-4 text-white" />
          </div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">{title}</h2>
        </div>

        <div className="space-y-5">
          {/* Search */}
          <div className="w-full">
            <Input
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              value={searchQuery}
              onValueChange={onSearchChange}
              startContent={
                <div className="p-1 rounded-md bg-gray-100 dark:bg-gray-800">
                  <Search className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                </div>
              }
              isClearable
              onClear={() => onSearchChange('')}
              className="w-full"
              variant="bordered"
              size="lg"
              classNames={{
                input: 'text-base',
                inputWrapper:
                  'border-gray-300 dark:border-gray-700 hover:border-indigo-400 dark:hover:border-indigo-600 transition-colors',
              }}
            />
          </div>

          {/* Filters Grid */}
          <div className={`grid grid-cols-1 ${gridColsClass} gap-4`}>
            {children}

            {/* Clear Filters Button */}
            {hasActiveFilters && (
              <div className="sm:col-span-1 flex items-end">
                <Button
                  variant="flat"
                  color="danger"
                  onPress={onClearFilters}
                  startContent={<X className="w-4 h-4" />}
                  className="w-full sm:w-auto whitespace-nowrap font-semibold mt-6"
                  size="lg"
                >
                  {clearFiltersLabel}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FiltersBar;

